import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  type ChatbotInstructions,
  type IAgentHandoffNotifySettings,
  type IChatbotField,
} from 'entities/Chatbot'
import { IntegrationKey } from 'entities/Integrations'
import type { MergeField } from 'entities/MergeField/model/MergeField'
import {
  addConversationNoteTemplate,
  addContactNoteTemplate,
  addTagTemplate,
  createActionChip,
  handoffToHumanTemplate,
  type IAgentAction,
  type ITagActionData,
  removeTagTemplate,
  sendSMSTemplate,
  stopConversationTemplate,
} from 'entities/TemplateActions/actions.tsx'
import { mergeFieldReplacer, MessageFieldStore } from 'widgets/MessageField'
import { EnumVariantMessageField } from 'widgets/MessageField/store/types'
import type { IAddContactNoteActionProps } from 'widgets/TemplateActions/addContactNote/addContactNote.tsx'
import { InstructionsSectionContent } from '../InstructionsSectionContent'

const makeMessageFieldStoreProps = (
  onFocus: () => void,
  styles: { minHeight: number; maxHeight: number }
) => ({
  placeholder: "Set your agent's role, response style, and add any specific instructions.",
  noCounter: true,
  styles: { ...styles },
  variant: EnumVariantMessageField.Clean,
  disableMergeFieldsFallback: true,
  resetOnUnmount: false,
  makeEvents: () => ({ onFocus }),
})

export class InstructionsSectionStore {
  error = ''
  private _disposeSync: IReactionDisposer | null = null
  readonly messageFieldStore: MessageFieldStore

  constructor(
    readonly entities: IEntitiesStore,
    private _agentId: number | null,
    private _instructions: ChatbotInstructions,
    styles = { minHeight: 200, maxHeight: 408 },
    private readonly _requestEnhanceOverride: (
      prompt: string,
      signal?: AbortSignal
    ) => Promise<string>
  ) {
    this.messageFieldStore = new MessageFieldStore(
      makeMessageFieldStoreProps(this.clearError, styles),
      entities
    )

    this.setInstructions(_instructions.instructions)

    makeAutoObservable(this)
    this.reactionSetInstructions()
  }

  reactionSetInstructions = () => {
    this._disposeSync?.()
    this._disposeSync = reaction(
      () => this.messageFieldStore.text,
      (text) => this._instructions.setInstructions(text)
    )
  }

  get instructions() {
    return this.messageFieldStore.text
  }

  private _extractMergeFieldsFromText(source: string) {
    return [...source.matchAll(/\{([^}]*)}/g)].reduce<Map<string, IChatbotField>>(
      (state, [template, key]) => {
        const fieldData = this.entities.mergeFieldStore.getMergeFieldInfoByTemplate(template)
        key = key.trim()
        const field = fieldData.mergeField
        if (field) state.set(key, { key, name: field.label, type: field.typeChatbotField })
        return state
      },
      new Map()
    )
  }

  get existMergeFields() {
    const text = this.messageFieldStore.text
    const fields = this._extractMergeFieldsFromText(text)

    for (const [, content] of text.matchAll(/"content":"((?:[^"\\]|\\.)*)"/g)) {
      for (const [key, field] of this._extractMergeFieldsFromText(content)) {
        fields.set(key, field)
      }
    }

    return fields
  }

  selectedContactFields: IChatbotField[] = []

  setContactFields = (fields: IChatbotField[]) => {
    this.selectedContactFields = fields
  }

  mergeContactFields = (fields: IChatbotField[]) => {
    this.selectedContactFields = [...this.selectedContactFields, ...fields]
  }

  get existMergeFieldsPayload() {
    const merged = new Map(this.existMergeFields)
    for (const field of this.selectedContactFields) {
      merged.set(field.key, field)
    }
    return Array.from(merged.values())
  }

  get mergeFieldConfig() {
    const hasCalendlyFields = !!this.entities.mergeFieldStore.getMergeFieldsIntegration(
      IntegrationKey.calendly
    )?.size
    const hasGoogleCalendarFields = !!this.entities.mergeFieldStore.getMergeFieldsIntegration(
      IntegrationKey.google_calendar
    )?.size

    const includeIntegrations = [IntegrationKey.hubspot]
    if (hasCalendlyFields) includeIntegrations.push(IntegrationKey.calendly)
    if (hasGoogleCalendarFields) includeIntegrations.push(IntegrationKey.google_calendar)

    return {
      isAllIntegration: true,
      includeIntegrations,
      excludeFieldsIntegrations: {
        [IntegrationKey.salesmessage]: ['number'],
      },
    }
  }

  get requestEnhance() {
    return this._requestEnhanceOverride
  }

  clearError = () => {
    this.error = ''
  }

  setError = (value: string) => {
    this.error = value
  }

  setInstructions = (value: string) => {
    this.messageFieldStore.setMessageData({
      message: value,
      attachments: [],
      isFocus: false,
      isReplace: true,
      isReset: false,
      replaceActionChips: true,
      isDisableReplaceMention: true,
    })
    this.error = ''
  }

  onAddMergeField = (field: MergeField, replaceContent?: string) => {
    if (!this.messageFieldStore.addContent) return
    const fieldContent = mergeFieldReplacer(this.entities, field.template) + '&nbsp;'

    this.messageFieldStore.addContent(fieldContent, replaceContent)
  }

  onAddAction = (action: IAgentAction) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.addContent(createActionChip(action) + '&nbsp;', '/')
  }
  replaceAction = (chip: HTMLSpanElement, action: IAgentAction) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.replaceActionChip(chip, createActionChip(action))
  }
  deleteAction = (chip: HTMLSpanElement) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.removeActionChip(chip)
  }

  openModal = () => {
    const modalId = nanoid()
    const snapshot = this._instructions.instructions

    const modalContentStore = new InstructionsSectionStore(
      this.entities,
      this._agentId,
      this._instructions,
      { minHeight: 452, maxHeight: 452 },
      this._requestEnhanceOverride
    )

    const handleCancel = () => {
      modalContentStore.dispose()
      this._instructions.setInstructions(snapshot)
      this.setInstructions(snapshot)
      modalStore.removeModal(modalId)
    }

    const handleSave = () => {
      modalContentStore.dispose()
      this.setInstructions(modalContentStore.instructions)
      modalStore.removeModal(modalId)
    }

    modalStore.addModal({
      id: modalId,
      title: 'Edit',
      width: 800,
      paddingContent: '0 24px 8px',
      onClose: handleCancel,
      primaryAction: {
        text: 'Save',
        onAction: handleSave,
      },
      ModalContent: InstructionsSectionContent,
      ModalContentProps: {
        store: modalContentStore,
        isExpanded: true,
      },
    })
  }

  dispose = () => {
    this._disposeSync?.()
  }

  getHandoffNotifySettings = () => {
    return this._instructions.handoff_notify_settings?.value
  }

  onAddHandoffToHumanAction = (data?: IAgentHandoffNotifySettings) => {
    this._instructions.setHandoffNotifySettings(data ? { value: data } : null)
    this.onAddAction(handoffToHumanTemplate.createAction())
  }
  onUpdateHandoffToHumanAction = (data?: IAgentHandoffNotifySettings) => {
    this._instructions.setHandoffNotifySettings(data ? { value: data } : null)
  }
  deleteHandoffToHumanAction = (chip: HTMLSpanElement) => {
    this.deleteAction(chip)
    if (!this.instructions.match(handoffToHumanTemplate.shortRegex)) {
      this._instructions.setHandoffNotifySettings(null)
    }
  }

  onAddTagAction = (data: ITagActionData) => {
    this.onAddAction(addTagTemplate.createAction(data))
  }

  onUpdateTagAction = (chip: HTMLSpanElement, data: ITagActionData) => {
    this.replaceAction(chip, addTagTemplate.createAction(data))
  }

  onRemoveTagAction = (data: ITagActionData) => {
    this.onAddAction(removeTagTemplate.createAction(data))
  }

  onUpdateRemoveTagAction = (chip: HTMLSpanElement, data: ITagActionData) => {
    this.replaceAction(chip, removeTagTemplate.createAction(data))
  }

  onAddContactNoteAction = (data: IAddContactNoteActionProps['data']) => {
    this.onAddAction(
      addContactNoteTemplate.createAction(
        data?.auto ? { auto: true } : { content: data?.message || '' }
      )
    )
  }
  onUpdateContactNoteAction = (chip: HTMLSpanElement, data: IAddContactNoteActionProps['data']) => {
    this.replaceAction(
      chip,
      addContactNoteTemplate.createAction(
        data?.auto ? { auto: true } : { content: data?.message || '' }
      )
    )
  }

  onAddConversationNoteAction = (data: IAddContactNoteActionProps['data']) => {
    this.onAddAction(
      addConversationNoteTemplate.createAction(
        data?.auto ? { auto: true } : { content: data?.message || '' }
      )
    )
  }

  onUpdateConversationNoteAction = (
    chip: HTMLSpanElement,
    data: IAddContactNoteActionProps['data']
  ) => {
    this.replaceAction(
      chip,
      addConversationNoteTemplate.createAction(
        data?.auto ? { auto: true } : { content: data?.message || '' }
      )
    )
  }

  onSendSMSAction = (data: IAddContactNoteActionProps['data']) => {
    this.onAddAction(
      sendSMSTemplate.createAction(data?.auto ? { auto: true } : { content: data?.message || '' })
    )
  }
  onUpdateSendSMSAction = (chip: HTMLSpanElement, data: IAddContactNoteActionProps['data']) => {
    this.replaceAction(
      chip,
      sendSMSTemplate.createAction(data?.auto ? { auto: true } : { content: data?.message || '' })
    )
  }
  onAddStopConversationAction = () => {
    this.onAddAction(stopConversationTemplate.createAction())
  }
}
